import { homePath } from '../../auth/permissions';
import { getRuntimeConfig } from '../../config/runtimeConfig';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Box, Boxes, Check, Eye, EyeOff, LockKeyhole, Mail, UsersRound, Warehouse } from 'lucide-react';
import { getLoginDestination, googleLogin, login } from '../../services/auth';
import { loadGoogleIdentity, requestGoogleCode } from '../../services/googleIdentity';
import portScene from '../../assets/images/login-port.jpg';
import worldMap from '../../assets/images/login-world-map.jpg';
import Toast from '../../components/Toast';
import '../warehouses/warehouses.css';
import './login.css';

const metrics = [
  [Warehouse, 'Warehouses'],
  [Boxes, 'Inventory'],
  [UsersRound, 'Customers'],
  [Box, 'Containers'],
];

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [recovery, setRecovery] = useState(false);
  const pending = useRef(false);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const googleAttempt = useRef(null);
  const toastTimer = useRef(null);

  function clearToast() {
    window.clearTimeout(toastTimer.current);
    setToast(null);
  }

  function showError(message) {
    window.clearTimeout(toastTimer.current);
    setToast({ message, tone: 'error' });
    toastTimer.current = window.setTimeout(() => setToast(null), 4000);
  }

  useEffect(() => {
    if (getRuntimeConfig().GOOGLE_CLIENT_ID) loadGoogleIdentity().catch(() => {});
    return () => {
      googleAttempt.current?.abort();
      window.clearTimeout(toastTimer.current);
    };
  }, []);

  async function signInWithGoogle() {
    if (pending.current) return;
    pending.current = true;
    setLoading(true);
    clearToast();
    setErrors({});
    const attempt = new AbortController();
    googleAttempt.current = attempt;
    try {
      const code = await requestGoogleCode(attempt.signal);
      if (attempt.signal.aborted) return;
      const user = await googleLogin({ code });
      if (attempt.signal.aborted) return;
      setPassword('');
      setSignedIn(true);
      navigate(getLoginDestination() || homePath(user), { replace: true });
    } catch (failure) {
      if (!attempt.signal.aborted) showError(failure.message || 'Unable to sign in with Google. Please try again.');
    } finally {
      pending.current = false;
      if (!attempt.signal.aborted) setLoading(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    if (pending.current) return;
    const submittedEmail = emailRef.current.value.trim();
    const submittedPassword = passwordRef.current.value;
    const next = {};
    if (!submittedEmail) next.email = 'Enter your email address.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(submittedEmail)) next.email = 'Enter a valid email address.';
    if (!submittedPassword) next.password = 'Enter your password.';
    setEmail(emailRef.current.value);
    setPassword(submittedPassword);
    setErrors(next);
    clearToast();
    if (Object.keys(next).length) {
      (next.email ? emailRef : passwordRef).current.focus();
      return;
    }
    pending.current = true;
    setLoading(true);
    try {
      const user = await login({ email: submittedEmail, password: submittedPassword });
      setPassword('');
      setSignedIn(true);
      navigate(getLoginDestination() || homePath(user), { replace: true });
    } catch (failure) {
      showError(failure.message || 'Unable to sign in. Please try again.');
    } finally {
      pending.current = false;
      setLoading(false);
    }
  }

  return <main className="login-page">
    <div className="login-scene" aria-hidden="true">
      <img className="login-photo" src={portScene} alt="" />
      <img className="login-map" src={worldMap} alt="" />
      <div className="login-wash" />
    </div>
    <section className="login-panel" aria-labelledby="login-heading">
      <a className="login-brand" href="/login" aria-label="Import ERP home"><span className="brand-mark"><Box size={20} strokeWidth={2} /></span>IMPORT ERP</a>
      <div className="login-content">
        <h1 id="login-heading">Orchestrating<br />global trade with<br /><span>effortless precision.</span></h1>
        <p className="login-description">Unified multi-modal booking, autonomous customs reconciliation, and programmatic letters of credit for the world’s most demanding commerce networks.</p>
        {signedIn ? <div className="login-success" role="status"><Check size={20} /><div><strong>You’re signed in.</strong><p>Your workspace is not available yet. Please contact your administrator.</p></div></div> :
          <>
            <div className="social-row">
              <button type="button" className="social-button" disabled={loading} onClick={signInWithGoogle}>
                <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Continue with Google
              </button>
              <button type="button" className="social-button" disabled title="Apple sign-in is not available yet">
                <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path fill="#111827" d="M16.37 12.23c-.03-3.04 2.48-4.5 2.59-4.57-1.41-2.06-3.61-2.34-4.39-2.37-1.87-.19-3.65 1.1-4.6 1.1-.95 0-2.42-1.07-3.98-1.04-2.05.03-3.93 1.19-4.98 3.02-2.12 3.68-.54 9.13 1.52 12.12 1.01 1.46 2.21 3.1 3.79 3.04 1.52-.06 2.09-.98 3.93-.98 1.84 0 2.35.98 3.96.95 1.64-.03 2.67-1.49 3.67-2.96 1.16-1.7 1.64-3.35 1.67-3.44-.04-.01-3.2-1.23-3.23-4.87zM13.5 3.8c.84-1.02 1.4-2.44 1.25-3.85-1.21.05-2.67.8-3.54 1.82-.78.9-1.46 2.35-1.28 3.74 1.35.1 2.73-.69 3.57-1.71z"/></svg>
                Continue with Apple
              </button>
            </div>
            <p className="login-divider">OR SIGN IN WITH EMAIL</p>
            <form onSubmit={submit} noValidate aria-busy={loading}>
              <div className="login-field">
                <label htmlFor="email">Email Address</label>
                <div className="input-shell input-end"><input ref={emailRef} id="email" name="email" type="email" autoComplete="username" placeholder="Enter email address" required value={email} disabled={loading} onChange={e => { setEmail(e.target.value); setErrors(old => ({ ...old, email: undefined })); }} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} /><Mail size={14} /></div>
                {errors.email && <p className="field-error" id="email-error">{errors.email}</p>}
              </div>
              <div className="login-field">
                <div className="field-head"><label htmlFor="password">Password</label><button type="button" className="text-button" onClick={() => setRecovery(!recovery)} aria-expanded={recovery} aria-controls="recovery-help">Forgot Password?</button></div>
                <div className="input-shell"><LockKeyhole size={14} /><input ref={passwordRef} id="password" name="password" type={visible ? 'text' : 'password'} autoComplete="current-password" placeholder="Enter password" required value={password} disabled={loading} onChange={e => { setPassword(e.target.value); setErrors(old => ({ ...old, password: undefined })); }} aria-invalid={Boolean(errors.password)} aria-describedby={errors.password ? 'password-error' : undefined} /><button className="password-toggle" type="button" onClick={() => setVisible(!visible)} aria-label={visible ? 'Hide password' : 'Show password'} aria-pressed={visible}>{visible ? <EyeOff size={15} /> : <Eye size={15} />}</button></div>
                {errors.password && <p className="field-error" id="password-error">{errors.password}</p>}
              </div>
              {recovery && <p id="recovery-help" className="login-notice" role="status">Please contact your workspace administrator to reset your password.</p>}
              <button className="sign-in-button" disabled={loading} type="submit">{loading ? <><span className="login-spinner" />Signing in…</> : <>Sign In<ArrowRight size={16} /></>}</button>
            </form>
          </>}
        <ul className="login-metrics" aria-label="Workspace areas">{metrics.map(([Icon, label]) => <li key={label}><Icon size={22} strokeWidth={1.7} /><span>{label}</span></li>)}</ul>
      </div>
    </section>
    <Toast toast={toast} onClose={clearToast} />
  </main>;
}
